import { useEffect, useState } from 'react';
import type { AdminTicketDetail } from '../../../domain/superadmin/types';
import type { TicketStatus } from '../../../domain/support/guards';
import { TICKET_STATUSES } from '../../../domain/support/guards';
import { Button, Select, Textarea } from '../../../components/ui';
import Modal from '../../../components/ui/Modal';
import Spinner from '../../../components/ui/Spinner';
import { formatDateTime } from '../../../utils/formatters';
import { TICKET_STATUS_LABELS } from './ticketStatusLabels';

interface AdminTicketDetailModalProps {
    ticketId: string | null;
    detail: AdminTicketDetail | null;
    isOpen: boolean;
    isLoading: boolean;
    isSaving: boolean;
    onClose: () => void;
    onReply: (ticketId: string, body: string) => Promise<void>;
    onUpdateStatus: (ticketId: string, status: TicketStatus) => Promise<void>;
}

export function AdminTicketDetailModal({
    ticketId,
    detail,
    isOpen,
    isLoading,
    isSaving,
    onClose,
    onReply,
    onUpdateStatus,
}: AdminTicketDetailModalProps) {
    const [replyBody, setReplyBody] = useState('');
    const [status, setStatus] = useState<TicketStatus>('open');

    useEffect(() => {
        if (detail) setStatus(detail.status);
    }, [detail]);

    useEffect(() => {
        if (!isOpen) setReplyBody('');
    }, [isOpen]);

    const isDetailReady = detail?.id === ticketId;
    const isClosed = isDetailReady && detail.status === 'closed';
    const canReply = isDetailReady && !isClosed && replyBody.trim().length > 0;

    const handleReply = async () => {
        if (!ticketId || !canReply) return;
        await onReply(ticketId, replyBody.trim());
        setReplyBody('');
    };

    const handleStatusChange = async (next: TicketStatus) => {
        if (!ticketId || next === detail?.status) return;
        setStatus(next);
        await onUpdateStatus(ticketId, next);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <Modal.Header onClose={onClose}>
                {isDetailReady ? detail.subject : 'Ticket'}
            </Modal.Header>
            <Modal.Body>
                {isLoading && (
                    <div className="flex justify-center py-12">
                        <Spinner color="primary" size="lg" />
                    </div>
                )}

                {!isLoading && isDetailReady && (
                    <>
                        <div className="grid gap-3 text-sm sm:grid-cols-2">
                            <p>
                                <span className="text-secondary">Organização: </span>
                                <span className="text-default">{detail.orgName}</span>
                            </p>
                            <p>
                                <span className="text-secondary">Solicitante: </span>
                                <span className="text-default">{detail.requesterEmail}</span>
                            </p>
                        </div>

                        <Select
                            label="Status"
                            selectSize="sm"
                            value={status}
                            options={TICKET_STATUSES.map((s) => ({
                                value: s,
                                label: TICKET_STATUS_LABELS[s],
                            }))}
                            onChange={(e) => void handleStatusChange(e.target.value as TicketStatus)}
                            disabled={isSaving}
                        />

                        <div className="space-y-3">
                            <p className="text-sm font-medium text-default">Conversa</p>
                            <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-subtle p-3">
                                {detail.messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={
                                            msg.authorRole === 'staff'
                                                ? 'ml-8 rounded-lg bg-primary/10 p-3'
                                                : 'mr-8 rounded-lg bg-surface-subtle p-3'
                                        }
                                    >
                                        <p className="text-xs text-secondary">
                                            {msg.authorRole === 'staff' ? 'Equipe' : 'Cliente'} ·{' '}
                                            {formatDateTime(msg.createdAt)}
                                        </p>
                                        <p className="mt-1 whitespace-pre-wrap text-sm text-default">
                                            {msg.body}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {!isClosed && (
                            <Textarea
                                label="Responder"
                                value={replyBody}
                                onChange={(e) => setReplyBody(e.target.value)}
                                placeholder="Escreva sua resposta..."
                                minRows={4}
                                disabled={isSaving}
                            />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Fechar
                </Button>
                {isDetailReady && !isClosed && (
                    <Button onClick={() => void handleReply()} disabled={!canReply || isSaving}>
                        {isSaving ? 'Enviando...' : 'Enviar resposta'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
